import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { LiquiditySeries } from "@kundeninfo/api-client";
import { useDiagramm, useDiagrammThema } from "@kundeninfo/ui/diagramm";
import { chartAriaLabel, chartLayoutKey, chartOption, legendEntries, windowFromZoom } from "./liquidityModel.js";
import type { DetailWindow } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

const sameWindow = (a: DetailWindow, b: DetailWindow): boolean => a?.from === b?.from && a?.to === b?.to;

/**
 * DAS DIAGRAMM (Spec „Diagramm“, Empfehlung 598): drei Abschnitte in EINER Instanz über `@kundeninfo/ui/diagramm`, die Option rechnet
 * `liquidityModel.ts`. Jedes Neuzeichnen geht über `setOption`, der Knoten bleibt, die Rollposition der Seite auch.
 *
 * `useDiagramm` mischt Optionen und ECharts entfernt dabei nichts: Bei einem neuen Abschnittsschlüssel leert ein Layout-Effekt die
 * Instanz, bevor der Effekt des Bausteins die neue Option setzt (gemessen: sonst bleiben nach „5 Jahre“ 3 `grid` und 42 Serien stehen).
 * Ein Fenster und der Rahmen gehören dem Bereich (`window`, `frameMode`), Gesten melden es über `onWindow`, nie per Mausrad.
 */
export function LiquidityChart(p: { series: LiquiditySeries; window: DetailWindow; frameMode: boolean; phone: boolean; nowMs: number;
  onWindow: (w: DetailWindow) => void }): ReactElement {
  const theme = useDiagrammThema();
  const box = useRef<HTMLDivElement | null>(null);
  const [widthPx, setWidthPx] = useState(0);
  useLayoutEffect(() => {
    const element = box.current;
    if (element === null) return undefined;
    const measure = (): void => { setWidthPx(element.clientWidth); };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => { observer.disconnect(); };
  }, []);

  const option = useMemo(() => (widthPx === 0 ? null : chartOption({ series: p.series, theme, widthPx, phone: p.phone, nowMs: p.nowMs,
    window: p.window, frameMode: p.frameMode })), [p.series, theme, widthPx, p.phone, p.nowMs, p.window, p.frameMode]);
  const d = useDiagramm(option, {
    datazoom: ({ startValue, endValue }) => {
      const next = windowFromZoom(p.series, startValue, endValue);
      if (!sameWindow(next, p.window)) p.onWindow(next);
    },
  });

  const layout = chartLayoutKey(p.series, p.phone);
  useLayoutEffect(() => { d.instanz?.clear(); }, [d.instanz, layout]);
  useEffect(() => {
    d.instanz?.dispatchAction({ type: "takeGlobalCursor", key: "dataZoomSelect", dataZoomSelectActive: p.frameMode });
  }, [d.instanz, p.frameMode]);

  return (
    <div className="liq__chart-block">
      {!p.phone && (
        <div className="liq__legend" role="group" aria-label={T.legendName}>
          {legendEntries(theme).map((e) => (
            <span key={e.key} className="liq__legend-entry">
              <span className={`liq__swatch liq__swatch--${e.form.toLowerCase()}`} aria-hidden="true"
                style={{ "--liq-swatch": e.color } as CSSProperties} />
              {e.label}
            </span>
          ))}
        </div>
      )}
      <div ref={box} className="liq__chart" role="img" aria-label={chartAriaLabel(p.series)}>
        <div ref={d.ref} className="liq__canvas" />
      </div>
    </div>
  );
}
